import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">分类未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const categoryArticles = articles.filter((a) => a.category === category.slug);
  const featuredInCategory = categoryArticles.filter((a) => a.featured).slice(0, 3);
  const otherArticles = categoryArticles.filter((a) => !a.featured);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51chigua.com/' },
      { '@type': 'ListItem', position: 2, name: category.name, item: `https://www.51chigua.com/category/${category.slug}` },
    ],
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${category.name} - 五一吃瓜网`,
    description: category.description,
    url: `https://www.51chigua.com/category/${category.slug}`,
    breadcrumb: { '@id': '#breadcrumb' },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <Navbar />

      <main>
        <section className="relative w-full pt-24 pb-12 md:pt-32 md:pb-16 px-4 md:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-950 transition-colors">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-foreground-950 font-medium">{category.name}</span>
            </nav>
            <h1 className="text-4xl md:text-5xl font-heading font-black text-foreground-950 leading-tight">
              {category.name}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-xl mx-auto leading-relaxed">
              {category.description}
            </p>
            <button className="mt-6 px-6 py-2.5 bg-foreground-950 text-background-50 rounded-full text-sm font-medium hover:bg-foreground-800 transition-all duration-300 whitespace-nowrap cursor-pointer">
              订阅该分类
            </button>
          </div>

          <div className="mt-10 max-w-5xl mx-auto">
            <img
              src={category.banner}
              alt={category.name}
              className="w-full rounded-3xl h-48 md:h-64 object-cover object-top"
            />
          </div>
        </section>

        {featuredInCategory.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-10 md:mb-14">
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950">
                本类精选
              </h2>
              <p className="text-sm text-foreground-500 md:pt-2">
                编辑严选 · 重磅追踪
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-11 gap-4 md:gap-5">
              {featuredInCategory.map((article, index) => {
                const isLeft = index === 0;
                return (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className={`group bg-background-100 rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all duration-300 flex flex-col ${
                      isLeft ? 'lg:col-span-5' : 'lg:col-span-3'
                    }`}
                  >
                    {isLeft ? (
                      <>
                        <div className="relative h-48 overflow-hidden">
                          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        </div>
                        <div className="p-5 flex-1 flex flex-col justify-between">
                          <h3 className="text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">{article.title}</h3>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
                          <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
                            <span>{article.publishDate}</span>
                            <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line"></i></span>{article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="p-5 flex-1 flex flex-col justify-between">
                          <h3 className="text-lg font-semibold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">{article.title}</h3>
                          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.summary}</p>
                          <div className="flex items-center gap-3 mt-4 text-xs text-foreground-500">
                            <span>{article.publishDate}</span>
                            <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line"></i></span>{article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}</span>
                          </div>
                        </div>
                        <div className="relative h-40 overflow-hidden">
                          <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        </div>
                      </>
                    )}
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        <section className="py-14 md:py-20 px-4 md:px-6 max-w-7xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 md:mb-14">
            全部文章
          </h2>

          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4">
            {categoryArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group block bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all duration-300 flex-shrink-0 w-[280px] md:w-[300px]"
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={article.image} alt={article.title} title={`五一吃瓜网 - ${article.title}`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  {article.exclusive && (
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-foreground-950 text-background-50 text-xs font-medium rounded-lg whitespace-nowrap">独家</span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">{article.title}</h3>
                  <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500 flex-wrap">
                    <span>{article.publishDate}</span>
                    <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line"></i></span>{article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}